import { INTEGRATION_KINDS, type IntegrationKind, IntegrationKindSchema } from '@developerz/domain';
import { createSignal, type JSX, onMount } from 'solid-js';
import { IntegrationsView } from '../components/IntegrationsView';
import { t } from '../i18n';
import {
  type ConnectInput,
  connectIntegration,
  disconnectIntegration,
  type IntegrationRow,
  integrationOauthLoginUrl,
  useIntegrations,
} from '../lib/integrations';
import { createMutation } from '../lib/mutation';
import { SESSION_TOKEN } from '../lib/session';
import { createToast } from '../lib/toast';

// Integrations catalog CONTAINER. Reads `GET /v1/integrations`; connect lives
// behind a form dialog, disconnect behind a destructive confirm. Two connect
// doors (#599): the paste form for every kind, and a "Connect with <kind>"
// redirect for kinds whose OAuth client creds are wired (via `oauth_kinds`).
// JSX lives in `IntegrationsView` (container/presenter split, file rules).

/**
 * Read the OAuth connect landing flags the callback at
 * `/auth/integrations/:kind/callback` redirects here with: `?connected=<kind>`
 * (success) or `?connect_error=<code>` (cancel/failure). Pure over the search
 * string so it's testable without a DOM (#599).
 */
export function readConnectLanding(search: string): {
  connected: IntegrationKind | null;
  connectErrorCode: string | null;
} {
  const params = new URLSearchParams(search);
  return {
    // Parsed, never cast: the search string is typeable, and an unknown kind
    // painted a success banner naming `⟦integrations.kind.<it>⟧` (plan 101
    // slice 17 row 17-S1). Unknown is no landing at all.
    connected: IntegrationKindSchema.safeParse(params.get('connected')).data ?? null,
    connectErrorCode: params.get('connect_error'),
  };
}

export function Integrations(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  const query = useIntegrations(token);

  const [connecting, setConnecting] = createSignal<IntegrationKind | null>(null);

  const [disconnecting, setDisconnecting] = createSignal<IntegrationRow | null>(null);

  // Failed disconnects surface as an error toast while the confirm stays open —
  // a destructive action must never silently no-op (AccountKeys pattern).
  const { toast, notify: notifyToast, dismiss: dismissToast } = createToast();

  // OAuth landing flags (#599), read at init (client only — SSR has no window)
  // so the banner paints on first render; onMount strips the param so a refresh
  // doesn't re-show it.
  const landing = typeof window === 'undefined' ? null : readConnectLanding(window.location.search);
  onMount(() => {
    if (landing && (landing.connected || landing.connectErrorCode)) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  });

  // Unknown/clamped provider codes fall back to the generic landing message.
  // Presence via `t.has` — the translator returns the `⟦key⟧` marker (not the
  // bare key) for a miss, so a `=== key` compare never matched and rendered
  // the marker to the user (#790).
  const landingError = (): string | null => {
    if (!landing?.connectErrorCode) return null;
    const key = `integrations.connect_error_code.${landing.connectErrorCode}`;
    return t.has(key) ? t(key) : t('integrations.connect_error_landing');
  };

  // The resync runs on BOTH outcomes and inside the pending window: a refused
  // connect may still have written a row, and the server's list is the only
  // thing that knows which.
  const connect = createMutation({
    call: (input: ConnectInput) => connectIntegration(input, token),
    fallback: t('integrations.connect_error'),
    onSuccess: () => setConnecting(null),
    onSettled: () => query.refetch(),
  });

  const disconnect = createMutation({
    call: (kind: IntegrationKind) => disconnectIntegration(kind, token),
    fallback: t('integrations.disconnect_error'),
    onSuccess: () => {
      setDisconnecting(null);
      void query.refetch();
    },
    onError: (message) => notifyToast(message, 'error'),
  });

  // One busy state for both writes, as when they shared a signal.
  const busy = (): boolean => connect.pending() || disconnect.pending();
  const connectError = connect.error;

  const submitConnect = (input: ConnectInput): void => void connect.run(input);

  const confirmDisconnect = (): void => {
    const row = disconnecting();
    if (!row) return;
    void disconnect.run(row.kind);
  };

  return (
    <IntegrationsView
      // No token = prod pre-SSO: gate the route so connect controls never render.
      hasSession={token !== null}
      token={token}
      query={query}
      kinds={INTEGRATION_KINDS}
      landingConnected={landing?.connected ?? null}
      landingError={landingError()}
      connecting={connecting()}
      busy={busy()}
      connectError={connectError()}
      onConnect={(kind) => {
        connect.clearError();
        setConnecting(kind);
      }}
      // Top-level navigation, NOT a fetch: the connect-tx cookie must be
      // first-party to survive the provider's redirect back (blocked as
      // third-party otherwise). The login leg lives on the API origin — a
      // relative path would 404 on app.<domain>. See
      // apps/api/src/integrations-oauth/flow.ts.
      onConnectOauth={(kind) => {
        window.location.assign(integrationOauthLoginUrl(kind));
      }}
      onSubmitConnect={submitConnect}
      onCloseConnect={() => setConnecting(null)}
      disconnecting={disconnecting()}
      onAskDisconnect={setDisconnecting}
      onConfirmDisconnect={confirmDisconnect}
      onCloseDisconnect={() => setDisconnecting(null)}
      toast={toast()?.message ?? null}
      onToastDismiss={dismissToast}
    />
  );
}
